/**
 * @license
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
//
// Vendored from rxfire 6.1.0 / packages/rxfire/performance/index.ts.
// rxfire has stagnated (last meaningful commit 2024-12-12) and pins firebase
// peer to ^9||^10||^11 — vendoring decouples us from those pins. Behavior is
// identical to upstream rxfire.

import { EMPTY, MonoTypeOperatorFunction, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

/**
 * Internal — emits an empty value, lays down a `performance.mark` on
 * subscribe, and another mark + a `performance.measure` on unsubscribe. The
 * elapsed time between marks lands as a User Timing entry, which Firebase
 * Performance picks up and reports.
 */
function trace$(traceId: string): Observable<void> {
  if (typeof window !== 'undefined' && window.performance) {
    const entries = window.performance.getEntriesByName(traceId, 'measure') ?? [];
    const startMarkName = `_${traceId}Start[${entries.length}]`;
    const endMarkName = `_${traceId}End[${entries.length}]`;
    return new Observable<void>((emitter) => {
      window.performance.mark(startMarkName);
      emitter.next();
      return {
        unsubscribe: (): void => {
          window.performance.mark(endMarkName);
          window.performance.measure(traceId, startMarkName, endMarkName);
        },
      };
    });
  }
  return EMPTY;
}

/**
 * Operator — begin a trace named `name` on subscribe, end it on unsubscribe
 * or stream completion.
 */
export function trace<T>(name: string): MonoTypeOperatorFunction<T> {
  return (source$: Observable<T>) =>
    new Observable<T>((subscriber) => {
      const traceSubscription = trace$(name).subscribe();
      return source$
        .pipe(
          tap({
            next: () => traceSubscription.unsubscribe(),
            complete: () => traceSubscription.unsubscribe(),
          }),
        )
        .subscribe(subscriber);
    });
}

/**
 * Operator — begin a trace, end it as soon as `test(value)` returns true.
 * Optionally end on stream completion via `options.orComplete`.
 */
export function traceUntil<T>(
  name: string,
  test: (a: T) => boolean,
  options?: { orComplete?: boolean },
): MonoTypeOperatorFunction<T> {
  return (source$: Observable<T>) =>
    new Observable<T>((subscriber) => {
      const traceSubscription = trace$(name).subscribe();
      return source$
        .pipe(
          tap({
            next: (a) => {
              if (test(a)) traceSubscription.unsubscribe();
            },
            complete: () => {
              if (options?.orComplete) traceSubscription.unsubscribe();
            },
          }),
        )
        .subscribe(subscriber);
    });
}

/**
 * Operator — runs a trace while `test(value)` is true. Starts on first
 * matching value, stops the moment `test` returns false. Optionally ends on
 * stream completion via `options.orComplete`.
 */
export function traceWhile<T>(
  name: string,
  test: (a: T) => boolean,
  options?: { orComplete?: boolean },
): MonoTypeOperatorFunction<T> {
  return (source$: Observable<T>) =>
    new Observable<T>((subscriber) => {
      let traceSubscription: Subscription | undefined;
      return source$
        .pipe(
          tap({
            next: (a) => {
              if (test(a)) {
                traceSubscription = traceSubscription ?? trace$(name).subscribe();
              } else {
                if (traceSubscription) traceSubscription.unsubscribe();
                traceSubscription = undefined;
              }
            },
            complete: () => {
              if (options?.orComplete && traceSubscription) traceSubscription.unsubscribe();
            },
          }),
        )
        .subscribe(subscriber);
    });
}

/** Operator — begin a trace, end it on stream completion. */
export function traceUntilComplete<T>(name: string): MonoTypeOperatorFunction<T> {
  return (source$: Observable<T>) =>
    new Observable<T>((subscriber) => {
      const traceSubscription = trace$(name).subscribe();
      return source$
        .pipe(tap({ complete: () => traceSubscription.unsubscribe() }))
        .subscribe(subscriber);
    });
}

/** Operator — begin a trace, end it on the first emission. */
export function traceUntilFirst<T>(name: string): MonoTypeOperatorFunction<T> {
  return (source$: Observable<T>) =>
    new Observable<T>((subscriber) => {
      const traceSubscription = trace$(name).subscribe();
      return source$
        .pipe(tap({ next: () => traceSubscription.unsubscribe() }))
        .subscribe(subscriber);
    });
}
